import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface MarketRentChartProps {
  location: string;
  chartType?: 'line' | 'stacked';
}

interface MarketRentData {
  year: number;
  rent_category: string;
  estimate: number | null;
  percent: number | null;
}

// Line or stacked area chart showing number of housing units by rent category over time
const MarketRentChart: React.FC<MarketRentChartProps> = ({ location, chartType = 'line' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    console.log('MarketRentChart: buildChart called for location:', location, 'chartType:', chartType);
    if (!canvasRef.current) {
      console.warn('MarketRentChart: No canvas ref available');
      return;
    }
    if (creatingRef.current) {
      console.warn('MarketRentChart: Chart creation already in progress');
      return;
    }
    creatingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) {
      creatingRef.current = false;
      setLoading(false);
      return;
    }

    try {
      await supabaseLocationService.initialize();
      const geofips = await supabaseLocationService.getGeofips(location);
      
      if (!geofips) {
        setError(`No geofips found for location: ${location}`);
        setHasData(false);
        creatingRef.current = false;
        setLoading(false);
        return;
      }

      // Fetch data from market_rent_percent table
      console.log('MarketRentChart: Fetching data for geofips:', geofips);
      const { data, error: fetchError } = await supabaseClient
        .from('market_rent_percent')
        .select('year, rent_category, estimate, percent')
        .eq('geofips', geofips)
        .order('year', { ascending: true })
        .order('rent_category', { ascending: true });

      console.log('MarketRentChart: Query result:', { dataLength: data?.length, error: fetchError });

      if (fetchError) {
        console.error('MarketRentChart: Error fetching data:', fetchError);
        setError(`Error fetching market rent data: ${fetchError.message}`);
        setHasData(false);
        creatingRef.current = false;
        setLoading(false);
        return;
      }

      // If no data, return early
      if (!data || data.length === 0) {
        setHasData(false);
        creatingRef.current = false;
        setLoading(false);
        return;
      }

      setHasData(true);

      // Categories are already standardized in the backend
      // Backend sends: 1500_and_over (2010-2014) and 1500_to_1999 (2015+)
      // No need to standardize - use categories as-is from database
      
      // Group data by rent_category and year
      const categoryData: Record<string, Record<number, number>> = {};
      const yearsSet = new Set<number>();
      
      data.forEach((row: MarketRentData) => {
        if (row.estimate !== null && row.estimate !== undefined) {
          const category = row.rent_category;
          
          // Skip invalid categories
          if (!category || category === 'other') {
            return;
          }
          
          if (!categoryData[category]) {
            categoryData[category] = {};
          }
          
          // Sum estimates if there are duplicates (shouldn't happen, but just in case)
          if (categoryData[category][row.year]) {
            categoryData[category][row.year] += row.estimate;
          } else {
            categoryData[category][row.year] = row.estimate;
          }
          
          yearsSet.add(row.year);
        }
      });

      const years = Array.from(yearsSet).sort((a, b) => a - b);

      // All years use 2015+ category structure for consistency
      // Old 2010-2014 categories "$500 to $749" and "$750 to $999" are combined into "$500 to $999"

      console.log('MarketRentChart: Processed data:', {
        years,
        categories: Object.keys(categoryData),
        categoryCounts: Object.fromEntries(Object.entries(categoryData).map(([k, v]) => [k, Object.keys(v).length]))
      });

      if (years.length === 0) {
        console.warn('MarketRentChart: No years with valid estimate data');
        setHasData(false);
        creatingRef.current = false;
        setLoading(false);
        return;
      }

      // Define color palette for rent categories
      // Note: "1500_and_over" (2010-2014) gets a distinct color from "1500_to_1999" (2015+)
      const palette = [
        'rgba(59, 130, 246, 0.8)',   // Blue - No Rent Paid
        'rgba(16, 185, 129, 0.8)',   // Green - Under $500
        'rgba(245, 158, 11, 0.8)',   // Yellow - $500-$999
        'rgba(239, 68, 68, 0.8)',    // Red - $1,000-$1,499
        'rgba(139, 92, 246, 0.8)',   // Purple - $1,500 and over (2010-2014)
        'rgba(168, 85, 247, 0.8)',    // Darker Purple - $1,500-$1,999 (2015+)
        'rgba(236, 72, 153, 0.8)',   // Pink - $2,000-$2,499
        'rgba(20, 184, 166, 0.8)',   // Teal - $2,500-$2,999
        'rgba(251, 146, 60, 0.8)',   // Orange - $3,000 and Over
      ];
      
      // Map categories to specific colors to ensure "1500_and_over" and "1500_to_1999" have different colors
      const getCategoryColor = (category: string, index: number): string => {
        const colorMap: Record<string, string> = {
          '1500_and_over': 'rgba(139, 92, 246, 0.8)',   // Purple for 2010-2014 "$1,500 and over"
          '1500_to_1999': 'rgba(168, 85, 247, 0.8)',   // Darker Purple for 2015+ "$1,500-$1,999"
        };
        return colorMap[category] || palette[index % palette.length];
      };

      // Define category order (lowest to highest rent)
      // Note: "1500_and_over" is for 2010-2014, "1500_to_1999" is for 2015+
      const categoryOrder = [
        'no_rent_paid',
        'under_500',
        '500_to_999',
        '1000_to_1499',
        '1500_and_over',  // 2010-2014 category
        '1500_to_1999',  // 2015+ category
        '2000_to_2499',
        '2500_to_2999',
        '3000_and_over'
      ];
      
      // Sort categories by predefined order, then alphabetically for any unmapped ones
      const categories = Object.keys(categoryData).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
      });
      
      // Helper function to format category labels for display
      const formatCategoryLabel = (category: string): string => {
        const labelMap: Record<string, string> = {
          'no_rent_paid': 'No Rent Paid',
          'under_500': 'Under $500',
          '500_to_999': '$500-$999',
          '1000_to_1499': '$1,000-$1,499',
          '1500_and_over': '$1,500 and Over',  // 2010-2014 category
          '1500_to_1999': '$1,500-$1,999',  // 2015+ category
          '2000_to_2499': '$2,000-$2,499',
          '2500_to_2999': '$2,500-$2,999',
          '3000_and_over': '$3,000 and Over'
        };
        return labelMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };
      
      // Create datasets for each rent category
      const isStacked = chartType === 'stacked';
      console.log('MarketRentChart: Creating chart with isStacked:', isStacked, 'categories:', categories.length);
      const datasets = categories.map((category, index) => {
        // For line charts, use null for missing/zero values so lines don't connect through gaps
        // For stacked charts, use 0 so the stack is continuous
        const values = years.map(year => {
          const value = categoryData[category][year];
          if (isStacked) {
            return value || 0; // Stacked charts need 0 for continuity
          } else {
            // Line charts: use null for missing data so lines don't draw through gaps
            return value && value > 0 ? value : null;
          }
        });
        const baseColor = getCategoryColor(category, index);
        return {
          label: formatCategoryLabel(category),
          data: values,
          backgroundColor: isStacked 
            ? baseColor 
            : baseColor.replace('0.8', '0.1'), // Lighter fill for line chart
          borderColor: baseColor,
          fill: isStacked, // Fill for stacked area chart
          tension: 0.4,
          borderWidth: isStacked ? 0 : 2, // No border for stacked, visible for line
          pointRadius: isStacked ? 0 : 3, // No points for stacked, show for line
          pointHoverRadius: isStacked ? 0 : 5,
          stack: isStacked ? 'stack1' : undefined, // Stack for stacked area chart
          spanGaps: false // Don't draw lines through null values (gaps)
        };
      });

      // Create the chart
      console.log('MarketRentChart: Creating new Chart instance with', datasets.length, 'datasets');
      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: years.map(String),
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: standardLegend,
            title: standardTitle(`Housing Units by Rent Category - ${location} (${chartType === 'stacked' ? 'Stacked' : 'Line'})`),
            tooltip: {
              mode: 'index',
              intersect: false,
              filter: function(tooltipItem: any) {
                // Only show tooltip if there's actual data (not null/zero)
                return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined && tooltipItem.parsed.y > 0;
              },
              callbacks: {
                label: function(context: any) {
                  const label = context.dataset.label || '';
                  const value = context.parsed.y;
                  if (value === null || value === undefined) {
                    return undefined; // Don't show label for null values
                  }
                  return `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} units`;
                }
              }
            }
          },
          scales: {
            x: {
              stacked: isStacked, // Stacked for stacked area chart
              title: { display: true, text: 'Year' },
            },
            y: {
              stacked: isStacked, // Stacked for stacked area chart
              title: { display: true, text: 'Number of Housing Units' },
              beginAtZero: true,
            },
          },
          interaction: { mode: 'index', intersect: false },
        },
      });

      console.log('MarketRentChart: Chart created successfully');
      creatingRef.current = false;
      setLoading(false);
    } catch (err: any) {
      console.error('MarketRentChart: Error building chart:', err);
      console.error('MarketRentChart: Error stack:', err.stack);
      setError(err.message || 'Error building chart');
      setHasData(false);
      creatingRef.current = false;
      setLoading(false);
    }
  }, [location, chartType]);

  useEffect(() => {
    console.log('MarketRentChart: useEffect triggered for chartType:', chartType, 'location:', location);
    
    // Destroy existing chart when chartType or location changes
    if (chartRef.current) {
      try {
        console.log('MarketRentChart: Destroying chartRef.current');
        chartRef.current.destroy();
        chartRef.current = null;
      } catch (err) {
        console.warn('MarketRentChart: Error destroying chart:', err);
      }
    }
    
    // Also check for any Chart.js instance on the canvas
    if (canvasRef.current) {
      try {
        const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
        if (existing) {
          console.log('MarketRentChart: Destroying existing Chart.js instance');
          existing.destroy();
        }
      } catch (err) {
        console.warn('MarketRentChart: Error destroying existing chart instance:', err);
      }
    }
    
    // Wait for canvas to be available before building chart
    const timer = setTimeout(() => {
      if (canvasRef.current) {
        console.log('MarketRentChart: Calling buildChart from useEffect');
        buildChart();
      } else {
        console.warn('MarketRentChart: Canvas ref still not available after timeout');
      }
    }, 50);
    
    const canvasNode = canvasRef.current;
    return () => {
      clearTimeout(timer);
      try {
        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = null;
        }
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch (err) {
        console.warn('MarketRentChart: Error in cleanup:', err);
      }
    };
  }, [buildChart, chartType, location]);

  // Always render canvas container so ref is available
  return (
    <div style={{ position: 'relative', height: '500px', width: '100%' }}>
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
          <div>Loading market rent data...</div>
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
          <div>{error}</div>
        </div>
      )}
      {hasData === false && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '40px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
          <div>No market rent data available for {location}.</div>
        </div>
      )}
      <canvas ref={canvasRef} style={{ position: 'relative', zIndex: 0, display: hasData ? 'block' : 'none' }} />
    </div>
  );
};

export default MarketRentChart;

